import { useMemo } from "react";

import { useApiQuery } from "@/api";
import useCurrentEnvironment from "@/hooks/use-environment";
import {
  UnifiedTrackerRow,
  UnifiedWtmReportResponse,
  WtmDetectionSourceOption,
  WtmLoadedBy,
} from "../types";

export function parseUnifiedTrackerRow(
  row: UnifiedTrackerRow,
): UnifiedTrackerRow {
  return {
    ...row,
    domains: row.domains ?? [],
    pageDomains: row.pageDomains ?? [],
    pageResolutionCount: row.pageResolutionCount ?? 0,
    firstDetectedAt: row.firstDetectedAt ? new Date(row.firstDetectedAt) : null,
    lastDetectedAt: row.lastDetectedAt ? new Date(row.lastDetectedAt) : null,
  };
}

function parseUnifiedWtmReport(
  data: UnifiedWtmReportResponse,
): UnifiedWtmReportResponse {
  return { rows: (data.rows ?? []).map(parseUnifiedTrackerRow) };
}

export default function useFetchUnifiedWtmReport(options?: {
  enabled?: boolean;
  loadedBy?: WtmLoadedBy[];
  detectionSources?: WtmDetectionSourceOption[];
  /**
   * Shows the previous rows while new filters load. The caller must remount
   * on an environment switch, or the previous env's rows show until the new
   * env loads.
   */
  keepPreviousData?: boolean;
}) {
  const environment = useCurrentEnvironment();

  const result = useApiQuery(
    [
      "get_unified_wtm_report",
      {
        loadedBy: options?.loadedBy,
        detectionSources: options?.detectionSources,
      },
    ],
    {
      enabled: (options?.enabled ?? true) && !!environment?.ID,
      keepPreviousData: options?.keepPreviousData,
      retry: (failureCount, error) => {
        if (failureCount >= 2) {
          return false;
        }
        return (error as { retryable?: boolean }).retryable !== false;
      },
    },
  );

  const data = useMemo(
    () => (result.data ? parseUnifiedWtmReport(result.data) : result.data),
    [result.data],
  );

  return { ...result, data };
}
